import { useCallback } from 'react';

import { useSelector } from '@xstate5/react';
import { type SnapshotFrom } from 'xstate5';

import { type BundleMachineActorRef } from './bundle.machine';
import { MAX_BUNDLE_PRODUCTS } from './bundle.machine.constants';
import { type PendingProduct } from './types';

// ─────────────────────────────────────────────────────────────────────────────

export const useBundleMachine = (actorRef: BundleMachineActorRef) => {
  //
  // ─── State ───────────────────────────────────────────────────────────

  const selectedProducts = useSelector(actorRef, getSelectedProducts);
  const pendingProduct = useSelector(actorRef, getPendingProduct);
  const isSelecting = useSelector(actorRef, checkIfSelecting);
  const isAdding = useSelector(actorRef, checkIfAdding);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const openSelect = useCallback(() => {
    actorRef.send({ type: 'OPEN_SELECT' });
  }, [actorRef]);

  const selectProduct = useCallback(
    (selectedProduct: PendingProduct) => {
      actorRef.send({ type: 'SELECT', ...selectedProduct });
    },
    [actorRef],
  );

  const removeSelected = useCallback(
    (selectionId: string) => {
      actorRef.send({ type: 'REMOVE_SELECTED', selectionId });
    },
    [actorRef],
  );

  const confirmSelect = useCallback(() => {
    actorRef.send({ type: 'CONFIRM_SELECT' });
  }, [actorRef]);

  const closeSelect = useCallback(() => {
    actorRef.send({ type: 'CLOSE_SELECT' });
  }, [actorRef]);

  const addToBag = useCallback(() => {
    actorRef.send({ type: 'ADD_TO_BAG' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  const selectedCount = selectedProducts.length;

  // ─────────────────────────────────────────────────────────────────────

  return {
    selectedCount: selectedCount,
    canSelectMore: selectedCount < MAX_BUNDLE_PRODUCTS,
    selectedProducts,
    pendingProduct,
    isSelecting,
    isAdding,
    openSelect,
    selectProduct,
    confirmSelect,
    closeSelect,
    removeSelected,
    addToBag,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSelectedProducts(snapshot: Snapshot) {
  return snapshot.context.selectedProducts;
}

function getPendingProduct(snapshot: Snapshot) {
  return snapshot.context.pendingProduct;
}

function checkIfSelecting(snapshot: Snapshot) {
  return snapshot.matches({ ready: 'selecting' });
}

function checkIfAdding(snapshot: Snapshot) {
  return snapshot.matches({ ready: 'adding' });
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Snapshot = SnapshotFrom<BundleMachineActorRef>;
